
import { computed, defineComponent, PropType, ref, toRef, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import Sidebar from 'primevue/sidebar';
import Button from 'primevue/button';
import type { FlowSettings } from '@/types/flow-settings';
import { isAutomationSettings } from '@/types/flow-settings';
import { TypeCampaignEnum } from '@/types';
import { useFlowSettingsValidation } from '@/composables/flows/useFlowSettingsValidation';
import FlowSettingsAutomationForm from './FlowSettingsAutomation.vue';
import FlowSettingsBulkForm from './FlowSettingsBulk.vue';

export default defineComponent({
  name: 'FlowSettingsPanel',

  components: {
    Sidebar,
    Button,
    FlowSettingsAutomation: FlowSettingsAutomationForm,
    FlowSettingsBulk: FlowSettingsBulkForm,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    settings: {
      type: Object as PropType<FlowSettings | null>,
      default: null,
    },
    flowId: {
      type: Number,
      required: true,
    },
    flowType: {
      type: String as PropType<TypeCampaignEnum>,
      required: true,
    },
  },

  emits: ['close', 'update'],

  setup(props, { emit, expose }) {
    const { t } = useI18n();
    const store = useStore();
    const contentRef = ref<HTMLElement | null>(null);

    // Convert props to refs for the composable
    const settingsRef = toRef(props, 'settings');
    const flowTypeRef = toRef(props, 'flowType');

    // Validation errors state
    const formValidationErrors = ref<Record<string, string[]>>({});

    // Use validation composable
    const {
      validate: validateSettings,
      resetValidation,
      hasFieldError,
      getFieldErrors,
    } = useFlowSettingsValidation(settingsRef, flowTypeRef);

    // Wrapper to update errors state
    const validate = async () => {
      const result = await validateSettings();
      formValidationErrors.value = result.errors;
      return result;
    };

    const isAutomation = computed(() => {
      if (!props.settings) return props.flowType === TypeCampaignEnum.AUTOMATION;
      return isAutomationSettings(props.settings);
    });

    const handleUpdate = (updates: Partial<FlowSettings>) => {
      store.commit('flowEditor/updateFlowSettings', {
        id: props.flowId,
        type: props.flowType,
        settings: updates,
      });
      emit('update', updates);
    };

    /**
     * Scroll to a specific field in the panel
     */
    const scrollToField = async (fieldName: string) => {
      await nextTick();
      const fieldElement = document.querySelector(`[data-field="${fieldName}"]`);
      if (fieldElement) {
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    /**
     * Validate settings and scroll to first error if any
     */
    const validateAndScrollToError = async () => {
      const result = await validate();
      if (!result.isValid && result.firstErrorField) {
        await scrollToField(result.firstErrorField);
      }
      return result;
    };

    // Expose methods for parent component
    expose({
      validate,
      validateAndScrollToError,
      scrollToField,
      resetValidation,
    });

    return {
      t,
      contentRef,
      isAutomation,
      handleUpdate,
      formValidationErrors,
      hasFieldError,
      getFieldErrors,
    };
  },
});
